import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ProductVariationDocument } from '@packages/gql/generated/shopping/ProductVariationDocument';
import { ProductVariationFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductVariationFragmentFragmentDoc';
import type { TrackingBreadcrumbsFragmentFragmentDoc } from '@packages/gql/generated/shopping/TrackingBreadcrumbsFragmentFragmentDoc';
import {
  DownloadsCard,
  EfficiencyFlagList,
  LazyRender,
  Price,
  QuantitySelection,
  VariationSelect,
  WishlistButtonWithApi,
} from '@packages/modules';
import { AddToBasketButton } from '@packages/modules/src/AddToBasketButton';
import {
  Box,
  CardContent,
  CardHeader,
  ConditionalWrapper,
  DynamicYield,
  Grid,
  InlineNotification,
  Portal,
  Skeleton,
  Stack,
  SwogoContainer,
  Typography,
  useDeviceType,
  useI18n,
} from '@packages/shared';
import { useConfig } from '@packages/utilities';
import DOMPurify from 'isomorphic-dompurify';
import { useParams, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { Suspense, startTransition, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { getProductAndFallbackId } from '../../helpers/getProductAndFallbackId';
import { AddToBasketLayer } from '../AddToBasketLayer';
import { ArticleServices } from '../ArticleServices';
import { BonusPoints } from '../BonusPoints';
import { CollapsibleCard } from '../CollapsibleCard';
import { CompactProduct } from '../CompactProduct';
import { CustomerRatings } from '../CustomerRatings';
import { DeliveryInfoCard } from '../DeliveryInfoCard';
import { EcoLabelCard } from '../EcoLabelCard';
import { Flixmedia } from '../Flixmedia';
import { ImageGallery } from '../ImageGallery';
import { ImageGalleryColorDialog } from '../ImageGallery/ImageGalleryColorDialog';
import { InstallmentCalculatorCallToAction } from '../InstallmentCalculator/InstallmentCalculatorCallToAction';
import { InstallmentCard } from '../InstallmentCalculator/InstallmentCard';
import { LegalHintsCard } from '../LegalHintsCard';
import { LoadbeeCard } from '../LoadbeeCard';
import { OocvTable } from '../OocvTable';
import { PaymentTypes } from '../PaymentTypes';
import { ProductHeadline } from '../ProductHeadline';
import { ProductHintsCard } from '../ProductHintsCard';
import { PrudsysProductsRecommendations } from '../PrudsysProductsRecommendations';
import { RatingIconsVerified } from '../RatingIconsVerified';
import { Recommendations } from '../Recommendations';
import { SampleOrderLink } from '../SampleOrderLink';
import { ShareButtonGridItem } from '../ShareButtonGridItem';
import { SimilarProducts } from '../SimilarProducts';
import { StickyArea } from '../StickyArea';
import { TaxHint } from '../TaxHint';
import { Tracking } from '../Tracking';
import { getGlycerinWishlistProductPayload } from '../Tracking/helper/getGlycerinWishlistProductPayload';
import { VoucherPrice } from '../VoucherPrice';
import { productVariationMessages } from './messages';
import { SlotBoxDV } from '../SlotBoxDV';

/* GraphQL */ `
  query ProductVariation(
    $skipCliplister: Boolean! = false
    $skipAplus: Boolean! = false
    $locale: String!
    $lang: String!
    $productId: String!
    $fallbackId: String!
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      ...ProductVariationFragment
    }
  }
`;

/* GraphQL */ `
  fragment ProductVariationFragment on Product {
    akl
    # needed for aPlus
    hasServices
    # needed for aPlus
    itemOptionCommKey
    longDescription
    name
    orderNumber
    # needed to check if SampleOrder Data is available for the product
    sampleOrderType
    sellingPoints
    sku
    sizeHint
    aplus(language: $lang) @skip(if: $skipAplus) {
      ...ArticleServicesFragment
    }
    bonuspoints {
      ...BonusPointsFragment
    }
    brand {
      name
    }
    delivery {
      deliverySize
      ...TaxHintFragment
    }
    availability {
      state
      message
    }
    dimensions {
      values {
        displayText
      }
      ...DimensionFragment
    }
    installment(source: "pdv", paymentpause: false) {
      ...InstallmentCalculatorCallToActionFragment
      numberOfInstallments
    }
    oocv {
      ...OocvTableFragment
    }
    price {
      value
      valueOld
      currency
      ...ProductPriceFragment
      ...VoucherPriceFragment
      ...ArticleServicesCurrencyFragment
    }
    powerEfficiencyFlags {
      color
      level
      link
      text
      hasOldLabel
    }
    seoRatingIcons: seoRating(locale: $locale) {
      product {
        ...RatingIconsVerifiedFragment
      }
      ...RatingSeoResultFragment
    }
    tireEfficiency {
      link
      flags {
        level
        type
      }
    }
    ...AddToBasketFragment
    ...CompactProductFragment
    ...CustomerRatingsFragment
    ...DeliveryInfoCardFragment
    ...DownloadsCardFragment
    ...EcoLabelCardFragment
    ...EfficiencyFlagListFragment
    ...FlixmediaFragment
    ...GetGlycerinWishlistProductPayloadFragment
    ...HeadlineFragment
    ...ImageGalleryFragment
    ...ImageGalleryColorDialogFragment
    ...LegalHintsCardFragment
    ...LoadbeeFragment
    ...ProductHintsCardFragment
    ...PrudsysProductsRecommendations
    ...RecommendationsFragment
    ...SampleOrderLinkFragment
    ...SimilarRecoFragment
    ...TrackingFragment
    paymentTypes {
      keys
    }
    ...PaymentTypesFragment
  }
`;

export const ProductVariation = ({
  productData,
  breadcrumbData,
  kimRatingSlot,
  seoListSlot,
  searchOrigin,
}: {
  breadcrumbData: FragmentType<typeof TrackingBreadcrumbsFragmentFragmentDoc> | undefined | null;
  productData: FragmentType<typeof ProductVariationFragmentFragmentDoc>;
  kimRatingSlot: ReactNode;
  seoListSlot: ReactNode;
  searchOrigin: string;
}) => {
  const imageGalleryRef = useRef<HTMLDivElement>(null);
  const [selectedVariation, setSelectedVariation] = useState<string | null>(null);
  const { isDesktop, isMobile } = useDeviceType();

  const { language, locale } = useI18n();
  const { formatMessage } = useIntl();

  const params = useParams<{ akl: string }>();
  const searchParams = useSearchParams();
  const ref = searchParams?.get('ref') || null;

  const {
    dynamicYieldEnabled,
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
    product: {
      aplus: { enabled: aplusEnabled },
      cliplister: { enabled: cliplisterEnabled },
      flixmedia: { enabled: flixmediaEnabled },
      hasSwogo,
      voucherPrice: { enabled: voucherPriceEnabled },
    },
  } = useConfig();

  const [{ data }] = useQuery({
    query: ProductVariationDocument,
    pause: !selectedVariation,
    variables: {
      ...getProductAndFallbackId(params?.akl, selectedVariation!),
      locale,
      lang: language,
      skipCliplister: !cliplisterEnabled,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  const productVariation = getFragmentData(
    ProductVariationFragmentFragmentDoc,
    data?.product ?? productData,
  );

  return (
    <>
      {/* <Tracking /> needs to be first component in page render flow because of timing issues with glycerin tracking */}
      <Tracking
        productData={productVariation}
        navq={searchOrigin}
        breadCrumbData={breadcrumbData}
      />
      <Grid container item rowSpacing={0.5} columnSpacing={5.3}>
        <Grid item xs={12} md={6} lg={7}>
          <ImageGallery
            data={productVariation}
            ref={imageGalleryRef}
            colorPicker={
              <ImageGalleryColorDialog
                data={productVariation}
                onChange={(sku) => startTransition(() => setSelectedVariation(sku))}
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={5}>
          <Grid
            container
            spacing={1.5}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            sx={{
              px: { xs: 1.5, md: 1.5, lg: 0 },
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h4" component="h1">
                {productVariation.name}
              </Typography>
            </Grid>
            {productVariation.seoRatingIcons && productVariation.seoRatingIcons.product && (
              <Grid item xs={12}>
                <RatingIconsVerified data={productVariation.seoRatingIcons.product} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Price data={productVariation.price} />
              <TaxHint data={productVariation.delivery} />
              {
                /* Geizhals Einsprung -> VoucherPrice */
                voucherPriceEnabled && <VoucherPrice data={productVariation.price} />
              }
              {dynamicYieldEnabled && (
                <DynamicYield dataDyId="dy_info_socialproof_dv" sx={{ mt: 1 }} />
              )}
            </Grid>
            {productVariation.bonuspoints && (
              <Grid item xs={12}>
                <BonusPoints data={productVariation.bonuspoints} />
              </Grid>
            )}
            {productVariation.paymentTypes && (
              <Grid item xs={12}>
                <PaymentTypes data={productVariation} />
              </Grid>
            )}
            {productVariation.installment &&
              productVariation.installment.numberOfInstallments > 0 &&
              InstallmentCalculatorCallToActionLayout && (
                <Grid item xs={12}>
                  <InstallmentCalculatorCallToAction
                    installmentData={productVariation.installment}
                    basePrice={productVariation.price.value}
                  />
                </Grid>
              )}

            {/* TODO: maybe move the grid inside of EfficiencyFlagList, so we dont have to put tireEfficiency
             * and powerEfficiencyFlags AND EfficiencyFlagListFragment in the fragment
             */}
            {(productVariation.tireEfficiency || productVariation.powerEfficiencyFlags) && (
              <Grid item xs={12}>
                <EfficiencyFlagList data={productVariation} />
              </Grid>
            )}
            {dynamicYieldEnabled && <DynamicYield dataDyId="dv_flag" />}
            {hasSwogo && <SwogoContainer position={0} />}
            {/* TODO: ask how quantitySelection should be handled  */}
            <Grid item xs={12}>
              <Suspense>
                {/** negative margins are in here, so the dividers in QunatitySelection are displayed correctly */}
                <Box marginLeft={{ xs: -1.5, md: 0 }} marginRight={{ xs: -1.5, lg: 0 }}>
                  <QuantitySelection key={productVariation.sku} />
                </Box>
              </Suspense>
            </Grid>
            <Grid item xs={12}>
              <Suspense>
                {/* TODO: ask UX how margin bottom or spacings in general should be in variation select (its all over the place in zeplin at the moment) */}
                <Stack spacing={3} mb={2.5}>
                  {productVariation.dimensions.map(
                    (dimension, i) =>
                      dimension.values.length > 0 && (
                        <VariationSelect
                          dimensionData={dimension}
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          onChange={(variation) =>
                            startTransition(() => setSelectedVariation(variation))
                          }
                          selectedVariation={selectedVariation}
                          sampleOrderLink={
                            productVariation.sampleOrderType !== null ? (
                              <SampleOrderLink sx={{ mt: 1.5 }} data={productVariation} />
                            ) : null
                          }
                        />
                      ),
                  )}
                </Stack>
              </Suspense>
            </Grid>
            {productVariation.sizeHint && (
              <Grid item xs={12}>
                <InlineNotification severity="info">{productVariation.sizeHint}</InlineNotification>
              </Grid>
            )}
            <Grid item xs={12}>
              <DeliveryInfoCard data={productVariation} />
            </Grid>
            {aplusEnabled && productVariation.aplus && (
              <Grid item xs={12}>
                <ArticleServices
                  data={productVariation.aplus}
                  currencyData={productVariation.price}
                />
              </Grid>
            )}
            {isDesktop && (
              <Grid item xs={12}>
                <Suspense>
                  <Stack
                    sx={{
                      display: 'flex',
                      gap: 1,
                      flexDirection: 'row',
                    }}
                  >
                    <WishlistButtonWithApi
                      trackingProps={{
                        products: [
                          getGlycerinWishlistProductPayload({
                            data: productVariation,
                            refSearchParam: ref,
                          }),
                        ],
                      }}
                      sku={productVariation.sku}
                      productName={productVariation.name}
                      variant="contained"
                      sx={{ flexShrink: 0 }}
                      availability={productVariation.availability.state}
                    />
                    <AddToBasketButton data={productVariation}>
                      {(layerData, lineItem) => (
                        <AddToBasketLayer data={layerData} lineItem={lineItem} />
                      )}
                    </AddToBasketButton>
                  </Stack>
                </Suspense>
                {dynamicYieldEnabled && (
                  <DynamicYield dataDyId="dy_button_rabattaktion_dv" sx={{ mt: 1 }} />
                )}
              </Grid>
            )}
            <ShareButtonGridItem title={productVariation.name} text={productVariation.name} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <PrudsysProductsRecommendations data={productVariation} />
      </Grid>
      <Grid item xs={12}>
        <ConditionalWrapper
          condition={isMobile}
          wrapper={(ch) => (
            <LazyRender
              key={`product-details-${productVariation.sku}`}
              fallback={<Skeleton height={550} />}
            >
              {ch}
            </LazyRender>
          )}
        >
          <Box>
            {hasSwogo && <SwogoContainer position={1} />}
            <Typography variant="h2" sx={{ m: 2 }} component="header">
              {formatMessage(productVariationMessages.slotTitle)}
            </Typography>
            <SlotBoxDV>
              <Grid container columnSpacing={5.75} rowSpacing={1.5}>
                <Grid item xs={12} lg={7}>
                  <Stack spacing={1.5}>
                    <CollapsibleCard
                      cardActionText={{
                        collapseText: formatMessage(productVariationMessages.collapseCardText),
                        expandText: formatMessage(productVariationMessages.expandCardText),
                      }}
                      collapseChildren={
                        productVariation.oocv &&
                        productVariation.oocv.map((oocvTable, idx) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Stack sx={{ p: 2 }} spacing={1.5} key={idx}>
                            <OocvTable oocvTableData={oocvTable} />
                          </Stack>
                        ))
                      }
                      collapsedSize="6rem"
                    >
                      <CardHeader
                        sx={{ py: 1, px: 1.5, borderBottom: 1, borderColor: 'grey.main' }}
                        title={<ProductHeadline productData={productVariation} />}
                      />
                      <CardContent sx={{ pb: 0 }}>
                        <Stack gap={1}>
                          <Box component="ul" sx={{ m: 0, pl: 2 }} aria-labelledby="selling-points">
                            {productVariation.sellingPoints.map((sellingPoint, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Box component="li" sx={{ typography: 'body2' }} key={idx}>
                                {sellingPoint}
                              </Box>
                            ))}
                          </Box>
                          {productVariation.longDescription && (
                            <Typography
                              variant="body2" /* eslint-disable-next-line react/no-danger */
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(productVariation.longDescription),
                              }}
                            />
                          )}
                          {flixmediaEnabled &&
                            productVariation.brand?.name.toLowerCase() === 'dyson' && (
                              <Flixmedia productData={productVariation} />
                            )}
                        </Stack>
                      </CardContent>
                    </CollapsibleCard>
                    <LoadbeeCard data={productVariation} />
                  </Stack>
                </Grid>
                <Grid item xs={12} lg={5}>
                  <Stack spacing={1.5}>
                    <EcoLabelCard ecoLabelsData={productVariation} />
                    <ProductHintsCard productHintsData={productVariation} />
                    <LegalHintsCard legalHintsData={productVariation} />
                    <DownloadsCard data={productVariation} />

                    {productVariation.installment &&
                      productVariation.installment.numberOfInstallments > 0 &&
                      InstallmentCalculatorCallToActionLayout &&
                      InstallmentCalculatorCallToActionLayout.layout === 'Layout3' &&
                      InstallmentCalculatorCallToActionLayout.openCalculatorIn.type === 'Card' && (
                        <InstallmentCard
                          basePrice={productVariation.price.value}
                          cardID={
                            InstallmentCalculatorCallToActionLayout.openCalculatorIn.calculatorID
                          }
                        />
                      )}
                    {dynamicYieldEnabled && (
                      <>
                        <DynamicYield dataDyId="dv_badge" />
                        <DynamicYield dataDyId="dv-horizontal-banner" />
                      </>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </SlotBoxDV>
          </Box>
        </ConditionalWrapper>
      </Grid>
      <Grid item xs={12}>
        <Suspense>
          <Recommendations recosData={productVariation} />
        </Suspense>
      </Grid>
      <Grid item xs={12}>
        {productVariation.seoRatingIcons && (
          <Suspense>
            <CustomerRatings
              productData={productVariation}
              ratingResultData={productVariation.seoRatingIcons}
            />
          </Suspense>
        )}
      </Grid>
      <Grid item xs={12}>
        <SimilarProducts data={productVariation} />
      </Grid>
      {kimRatingSlot}
      {seoListSlot}
      <Portal>
        <StickyArea attachedNodeRef={imageGalleryRef}>
          <CompactProduct data={productVariation} withProductName={isDesktop} />
          <Box display="flex" flexDirection="column">
            <Stack direction="row" gap={1} alignItems="center" sx={{ bgcolor: 'common.white' }}>
              <WishlistButtonWithApi
                trackingProps={{
                  products: [
                    getGlycerinWishlistProductPayload({
                      data: productVariation,
                      refSearchParam: ref,
                    }),
                  ],
                }}
                variant="contained"
                sku={productVariation.sku}
                sx={{ flexShrink: 0 }}
                productName={productVariation.name}
                availability={productVariation.availability.state}
              />
              <AddToBasketButton data={productVariation}>
                {(layerData, lineItem) => <AddToBasketLayer data={layerData} lineItem={lineItem} />}
              </AddToBasketButton>
            </Stack>
            {dynamicYieldEnabled && (
              <DynamicYield dataDyId="dy_button_rabattaktion_dv" sx={{ mt: 1 }} />
            )}
          </Box>
        </StickyArea>
      </Portal>
    </>
  );
};
